import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from './data.service';
import { ToastService } from './toast.service';

/**
 * Sends the guest to the "please rescan" page - either immediately (the table couldn't be
 * resolved at boot) or once the given time has passed since the QR scan.
 * The scan time is stored as a wall-clock timestamp so it survives backgrounded/frozen tabs,
 * reloads and gateway redirects - it's checked at boot, when the tab becomes visible, on navigation
 * and by a single timeout armed for the exact expiry.
 */
@Injectable({
  providedIn: 'root'
})
export class RescanService {
  private readonly SCAN_TIME_KEY = 'scanned_at';
  // Don't interrupt an ongoing payment or its confirmation
  private readonly pathsWithoutRescan = ['/bill-paid', '/confirm-order', '/payment/'];

  private limitInMs: number;
  private timeoutId: number | null = null;
  private listening = false;
  private expired = false;

  private dataService = inject(DataService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);

  /** A fresh QR scan (not a reload or a gateway return) restarts the session. */
  public markScan(): void {
    localStorage.setItem(this.getScanTimeKey(), Date.now().toString());
  }

  public startTimer(limitInMs: number): void {
    this.limitInMs = limitInMs;
    if (!this.getScanTime()) {
      this.markScan();
    }

    if (!this.listening) {
      this.listening = true;
      // A frozen background tab delays the timeout, so re-check (and re-arm) when it comes back
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.check();
      });
      // bfcache restores don't fire visibilitychange reliably
      window.addEventListener('pageshow', () => this.check());
      // Expiry is deferred on payment pages - pick it up once the user leaves them
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => this.check());
    }
    this.check();
  }

  public redirectToRescan(): void {
    this.clearTimeout();
    this.router.navigate(['/rescan'], { replaceUrl: true });
  }

  private check(): void {
    if (this.expired || !this.limitInMs) return;
    this.clearTimeout();
    if (this.pathsWithoutRescan.some(path => window.location.pathname.startsWith(path))) return;

    const scanTime = this.getScanTime();
    const remainingInMs = scanTime ? scanTime + this.limitInMs - Date.now() : 0;
    if (remainingInMs > 0) {
      // Single timeout for the exact expiry instead of polling
      this.timeoutId = window.setTimeout(() => this.check(), remainingInMs);
      return;
    }

    this.expired = true;
    this.expireSession();
  }

  private expireSession(): void {
    this.redirectToRescan();
    this.toastService.error(this.translateService.instant('RESCAN.SESSION_EXPIRED'));
  }

  private clearTimeout(): void {
    if (this.timeoutId !== null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private getScanTime(): number | null {
    const value = Number(localStorage.getItem(this.getScanTimeKey()));
    return value > 0 ? value : null;
  }

  private getScanTimeKey(): string {
    return `${this.SCAN_TIME_KEY}_${this.dataService.getTableId()}`;
  }
}
